import { Fragment } from "react/jsx-runtime";
import Logo from "@/assets/images/logo.png";
import { usePathname } from "@/components/hooks/use-pathname";
import { Divider } from "@/components/shared/divider";
import { Bell, Question, SignOut } from "@/components/shared/icons";
import { Link } from "@/components/shared/link";
import { useRoute } from "@/components/shared/router";
import { Button, Dialog, DialogContent, Image, View } from "@/components/ui";
import { IconButton } from "@/components/ui/icon-button";
import { links } from "@/constants";
import { PosthogAnalytics } from "@/lib/monitoring/posthog";
import { handleOpenLink } from "@/lib/platform/index.web";
import { useAuthState } from "@/store/auth";
import { AuthModals } from "@/types/users";

import { useAuth } from "../../auth-provider";
import { GenerateMenu } from "../generate-menu";
import { GetTheAppNow } from "../get-the-app-now";
import { NavbarListItem } from "../navbar-list-item";
import { UserAvatar } from "../user-avatar";

export const NavbarMobile = ({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
}) => {
  const {
    data: { userInfo, unreadNotificationCount },
  } = useAuthState();
  const { isAuthenticated } = useAuth();
  const { push } = useRoute();
  const pathname = usePathname();
  const { showModal } = useAuthState();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        showClose={false}
        className="max-w-screen bg-brand-static flex h-dvh flex-col border-0 p-0"
      >
        <View className="color-brand-on-static flex h-full flex-1 flex-col py-4 font-light">
          <View className="mb-4 flex items-center justify-between px-4">
            <Image src={Logo} alt="Logo" width={60} height={28} />
            <IconButton
              variant="ghost"
              color="white"
              iconName="x"
              size="xl"
              onClick={() => setOpen(false)}
            />
          </View>

          <View className="flex flex-1 flex-col overflow-y-auto px-4">
            {isAuthenticated ? (
              <>
                <Link href="profile">
                  <NavbarListItem
                    icon={<UserAvatar />}
                    title={userInfo.firstName}
                    onClick={() => setOpen(false)}
                  />
                </Link>
                <Divider className="my-3 opacity-25" />
                <Link href="/notifications">
                  <NavbarListItem
                    icon={<Bell size={22} />}
                    badge={
                      unreadNotificationCount > 0 ? String(unreadNotificationCount) : undefined
                    }
                    title="Notification"
                    onClick={() => setOpen(false)}
                  />
                </Link>
              </>
            ) : (
              <>
                <Divider className="mb-3 opacity-25" />
                <View className="grid grid-cols-2 gap-2">
                  <Button
                    variant="secondary"
                    size="lg"
                    rounded="full"
                    className="border-brand-static-border border"
                    onClick={() => {
                      setOpen(false);
                      push({ pageKey: "login" });
                    }}
                    children="Sign In"
                  />
                  <Button
                    variant="primary"
                    size="lg"
                    color="CTA"
                    rounded="full"
                    className="border border-transparent"
                    onClick={() => {
                      setOpen(false);
                      push({ pageKey: "signup" });
                      PosthogAnalytics.track("user_sign_up", { path: pathname });
                    }}
                    children="Sign Up"
                  />
                </View>
              </>
            )}
            <Divider className="my-3 opacity-25" />
            <View onClick={() => setOpen(false)}>
              <GenerateMenu />
            </View>
            <Divider className="my-3 opacity-25" />
            <NavbarListItem
              icon={<Question size={22} />}
              title="Help"
              showTrailingIcon={false}
              onClick={() => {
                handleOpenLink(links.helpCenter);
              }}
            />
            {isAuthenticated ? (
              <Fragment>
                <NavbarListItem
                  icon={<SignOut size={22} />}
                  title="Logout"
                  showTrailingIcon={false}
                  onClick={() => {
                    setOpen(false);
                    showModal(AuthModals.LOGOUT);
                  }}
                />
                <Divider className="mt-3 opacity-25" />
              </Fragment>
            ) : null}
            <GetTheAppNow size="large" className="mt-auto pt-4" />
          </View>
        </View>
      </DialogContent>
    </Dialog>
  );
};
